'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Calendar, RotateCcw, Activity, Gauge, ChevronDown } from 'lucide-react';
import { ARCHETYPES } from '@/lib/archetypeSystem';
import WeekTracker from '@/components/WeekTracker';
import { TrainingAdvisoryCompact } from '@/components/TrainingAdvisory';
import TrainingBreakBanner from '@/components/TrainingBreakBanner';
import ProfileCard from '@/components/ProfileCard';
import DailyStatusRow from '@/components/dashboard/DailyStatusRow';
import type { DashboardData } from '@/lib/hooks/useDashboardData';

interface ProgramDashboardProps {
  data: DashboardData;
}

export default function ProgramDashboard({ data }: ProgramDashboardProps) {
  const [missedExpanded, setMissedExpanded] = useState(false);
  const {
    user,
    currentWeek,
    currentDay,
    programSelection,
    activeBreak,
    currentPhase,
    needsDeload,
    deloadReason,
    dismissedDeload,
    todayRecovery,
    intelligenceReport,
    archetypeNudge,
    updatingDay,
    recoveryDebt,
    handleEndBreak,
    handleDayClick,
    handleRefreshProgramState,
    startTodayWorkout,
    startDeloadedWorkout,
    setShowDayPicker,
    todayActivity,
    dailyCalories,
  } = data;

  // When ACWR is severe/dangerous AND deload is recommended, the TrainingAdvisory
  // already shows a REST DAY card.  Hide the "Start Deloaded" CTA to avoid
  // contradictory instructions (rest + deload at the same time).
  const restDayRecommended = needsDeload && recoveryDebt &&
    (recoveryDebt.debtLevel === 'severe' || recoveryDebt.acwrStatus === 'dangerous');

  if (!programSelection) return null;

  const skippedCount = programSelection.skippedDays?.length ?? 0;
  const hasSkipped = !activeBreak && skippedCount > 0;

  return (
    <>
      {/* Training Break Banner moved UP to app/page.tsx, above the persona
          switch. It used to live here, which meant only this one persona ever
          rendered it — while ALL of them hide the Start Workout CTA on an
          active break. On the other five, the primary action just vanished
          with nothing in its place and no way to end the trip. */}

      {/* START WORKOUT — Dominant CTA, classified-stamp framing */}
      {!activeBreak && (
        <section className="relative overflow-hidden rounded-xl border border-amber-500/40 bg-hud-ink/85 shadow-[0_0_40px_-16px_rgba(245,158,11,0.4)]">
          <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-500/80 to-transparent" />
          <span aria-hidden className="pointer-events-none absolute top-0 left-0 w-3 h-3 border-t border-l border-amber-500/60" />
          <span aria-hidden className="pointer-events-none absolute top-0 right-0 w-3 h-3 border-t border-r border-amber-500/60" />
          <span aria-hidden className="pointer-events-none absolute bottom-0 left-0 w-3 h-3 border-b border-l border-amber-500/60" />
          <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-3 h-3 border-b border-r border-amber-500/60" />

          {/* Header strip */}
          <header className="px-4 py-1.5 flex items-center justify-between border-b border-amber-500/15 font-mono text-[9px] uppercase tracking-[0.28em]">
            <div className="flex items-center gap-2 text-amber-500/90">
              <span className="w-1 h-1 rounded-full bg-hud-cyan animate-hud-blink" />
              <span>Today&apos;s Order</span>
            </div>
            <span className="text-slate-500 tabular-nums">
              W{currentWeek}<span className="text-slate-700"> · </span>D{currentDay}
            </span>
          </header>

          <div className="px-4 py-4 flex flex-col items-center text-center">
            {/* Phase label */}
            <div className={`font-mono text-[10px] uppercase tracking-[0.22em] mb-3 ${currentPhase.color}`}>
              {currentPhase.name}
            </div>

            {/* Status callouts */}
            {needsDeload && !dismissedDeload && (
              <div className="mb-3 inline-flex items-center gap-1.5 px-2 py-0.5 border border-amber-500/40 bg-amber-500/[0.06] font-mono text-[10px] uppercase tracking-[0.18em] text-amber-400">
                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-hud-blink" />
                Deload: {deloadReason}
              </div>
            )}
            {!todayRecovery && !restDayRecommended && !needsDeload && (
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-3">
                Recovery not logged today
              </div>
            )}

            {/* CTAs */}
            <div className="flex flex-wrap items-center justify-center gap-2">
              {restDayRecommended ? (
                <>
                  <button
                    onClick={startDeloadedWorkout}
                    className="px-5 py-2.5 rounded-md bg-slate-950 border border-amber-500/50 text-amber-400 font-mono font-bold tracking-[0.18em] text-[11px] uppercase hover:bg-amber-500/10 transition"
                  >
                    Train Anyway (Deloaded)
                  </button>
                  <button
                    onClick={() => startTodayWorkout(false, true)}
                    className="px-4 py-2 rounded-md bg-slate-950 border border-slate-800 text-slate-500 font-mono font-semibold tracking-[0.18em] text-[10px] uppercase hover:text-slate-300 hover:border-slate-700 transition"
                  >
                    Full Intensity
                  </button>
                </>
              ) : needsDeload && !dismissedDeload ? (
                <>
                  <button
                    onClick={startDeloadedWorkout}
                    className="px-6 py-3 rounded-md bg-amber-500 text-hud-ink font-mono font-bold tracking-[0.22em] text-sm uppercase hover:bg-amber-400 transition shadow-[0_0_24px_-6px_rgba(245,158,11,0.7)]"
                  >
                    Start Deloaded
                  </button>
                  <button
                    onClick={() => startTodayWorkout(false, true)}
                    className="px-5 py-2.5 rounded-md bg-slate-950 border border-slate-700 text-slate-200 font-mono font-semibold tracking-[0.18em] text-[11px] uppercase hover:bg-slate-900 transition"
                  >
                    Full Intensity
                  </button>
                </>
              ) : (
                <button
                  onClick={() => startTodayWorkout()}
                  className="px-10 py-3.5 rounded-md bg-amber-500 text-hud-ink font-mono font-bold tracking-[0.22em] text-sm uppercase hover:bg-amber-400 transition shadow-[0_0_24px_-6px_rgba(245,158,11,0.7)]"
                >
                  Start Workout
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* DAILY VITALS — calories, steps, protein at a glance */}
      <DailyStatusRow
        userId={user?.uid}
        todayActivity={todayActivity}
        dailyCalories={dailyCalories}
      />

      {/* OPERATOR PROFILE — identity context, demoted from top so action leads */}
      <ProfileCard compact={true} showEditLink={true} streak={intelligenceReport?.currentState?.weeklySessionCount} />

      {/* PROGRAM CONTEXT — Week tracker, navigation, readiness */}
      {!activeBreak && (
        <section className="relative overflow-hidden rounded-xl border border-slate-800/80 bg-hud-ink/80">
          <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-600/60 to-transparent" />
          <span aria-hidden className="pointer-events-none absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l border-slate-700" />
          <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-slate-700" />

          <div className="px-4 py-3">
            {/* Header strip — phase + nav buttons */}
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-0.5 h-3 ${currentPhase.color.replace('text', 'bg')}`} aria-hidden />
                <span className={`font-mono text-[10px] uppercase tracking-[0.22em] font-bold ${currentPhase.color}`}>
                  {currentPhase.name}
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600 hidden sm:inline">
                  Focus · <span className="text-slate-400 capitalize">{currentPhase.focus}</span>
                </span>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {user && (
                  <button
                    onClick={() => setShowDayPicker(true)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-700 text-slate-400 font-mono text-[9px] uppercase tracking-[0.18em] hover:border-amber-500/60 hover:text-amber-400 transition"
                  >
                    <Calendar className="w-3 h-3" aria-hidden />
                    Nav
                  </button>
                )}
                <Link
                  href="/programs/change"
                  className="px-2 py-1 rounded border border-amber-500/50 text-amber-400 font-mono text-[9px] uppercase tracking-[0.18em] hover:bg-amber-500/10 transition"
                >
                  Change
                </Link>
              </div>
            </div>

            {/* Week tracker */}
            <WeekTracker
              week={currentWeek}
              currentDay={currentDay}
              userId={user?.uid}
              programSelection={programSelection}
              onDayClick={handleDayClick}
              onRefresh={handleRefreshProgramState}
            />

            {/* Footer — advisory + secondary links */}
            <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-800">
              <div className="flex items-center gap-3 min-w-0">
                <TrainingAdvisoryCompact />
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 font-mono text-[10px] uppercase tracking-[0.18em]">
                {/* Use Next Link instead of raw <a href> — under Capacitor's
                    WebView (capacitor:// scheme) raw anchors don't always
                    resolve cleanly to Next routes and silently no-op. Link
                    triggers client-side routing through the Next router
                    which works in both web and native shells. */}
                <Link href="/travel" className="text-hud-cyan hover:underline">Travel</Link>
                <span className="text-slate-700">·</span>
                <Link href="/injury" className="text-amber-400 hover:underline">Injury</Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ARCHETYPE NUDGE - Show when user is close to unlocking */}
      {archetypeNudge && (
        <Link
          href="/barracks"
          className={`relative block overflow-hidden rounded-xl border transition hover:bg-amber-500/[0.04] ${
            archetypeNudge.urgency === 'high'
              ? 'border-amber-500/70 bg-amber-500/[0.06]'
              : archetypeNudge.urgency === 'medium'
              ? 'border-amber-500/40 bg-hud-ink/85'
              : 'border-slate-700/60 bg-hud-ink/80'
          }`}
        >
          <span aria-hidden className={`absolute top-0 bottom-0 left-0 w-1 ${
            archetypeNudge.urgency === 'high' ? 'bg-amber-500 animate-hud-blink' :
            archetypeNudge.urgency === 'medium' ? 'bg-amber-500' : 'bg-slate-600'
          }`} />

          <div className="pl-4 pr-3 py-3 flex items-center gap-3">
            <div className="relative w-14 h-14 rounded overflow-hidden border border-amber-500/50 flex-shrink-0">
              <img
                src={ARCHETYPES[archetypeNudge.archetypeId]?.image || '/operator.png'}
                alt={archetypeNudge.archetypeName}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-hud-ink/70 to-transparent" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className={`font-mono text-[9px] uppercase tracking-[0.22em] font-bold ${
                  archetypeNudge.urgency === 'high' ? 'text-amber-400' : 'text-amber-500/80'
                }`}>
                  {archetypeNudge.urgency === 'high' ? 'Almost There' : archetypeNudge.urgency === 'medium' ? 'Keep Pushing' : 'On Track'}
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500 tabular-nums">
                  {archetypeNudge.overallProgress}%
                </span>
              </div>
              <div className="font-mono text-[13px] font-bold text-white truncate">
                {archetypeNudge.archetypeName}
              </div>
              <p className="font-mono text-[10px] text-slate-400 line-clamp-2 mt-0.5 leading-relaxed">
                {archetypeNudge.message}
              </p>
              {/* Progress bar */}
              <div className="mt-2 h-1.5 bg-slate-900 border border-slate-800 rounded overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    archetypeNudge.urgency === 'high'
                      ? 'bg-amber-400 shadow-[0_0_6px_rgba(245,158,11,0.5)]'
                      : 'bg-gradient-to-r from-amber-500 to-amber-300'
                  }`}
                  style={{ width: `${archetypeNudge.overallProgress}%` }}
                />
              </div>
            </div>

            <span className="text-amber-400 text-lg flex-shrink-0">→</span>
          </div>
        </Link>
      )}

      {/* MISSED DRILLS — demoted to bottom and collapsed. Negative-emotion card
          shouldn't lead. Renders only when there are skipped workouts. */}
      {hasSkipped && (
        <section className="relative overflow-hidden rounded-xl border border-slate-800/80 bg-hud-ink/70">
          <span aria-hidden className="absolute top-0 bottom-0 left-0 w-1 bg-amber-500/60" />
          <button
            onClick={() => setMissedExpanded(v => !v)}
            className="w-full pl-4 pr-3 py-2.5 flex items-center gap-3 text-left hover:bg-amber-500/[0.03] transition"
            aria-expanded={missedExpanded}
          >
            <RotateCcw className="w-3.5 h-3.5 text-amber-400/80 flex-shrink-0" aria-hidden />
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-amber-400/80">Missed Drills</span>
              <span className="font-mono text-[11px] text-slate-300 tabular-nums">
                {skippedCount}
              </span>
            </div>
            <ChevronDown
              className={`w-3.5 h-3.5 text-slate-500 transition-transform ${missedExpanded ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>

          {missedExpanded && (
            <div className="border-t border-slate-800/80 px-4 py-3 space-y-2">
              <div className="font-mono text-[12px] text-slate-300">
                {skippedCount > 10
                  ? 'You\'ve got a backlog. Consider restarting your program for a clean slate.'
                  : 'Pick a missed day and do it today, or dismiss them all.'}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowDayPicker(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-amber-500/40 text-amber-400 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-amber-500/10 transition"
                >
                  <Calendar className="w-3 h-3" aria-hidden /> Pick one
                </button>
                <button
                  onClick={async () => {
                    if (!user || !programSelection) return;
                    if (!confirm('Clear all missed workouts? They will no longer show as skipped.')) return;
                    try {
                      const { saveUserProgramSelection } = await import('@/lib/programManagement');
                      await saveUserProgramSelection(user.uid, {
                        ...programSelection,
                        skippedDays: [],
                      });
                      handleRefreshProgramState();
                    } catch (err) {
                      console.error('Failed to dismiss missed workouts:', err);
                    }
                  }}
                  className="px-3 py-1.5 rounded border border-slate-700 text-slate-400 font-mono text-[10px] uppercase tracking-[0.18em] hover:text-white hover:border-slate-600 transition"
                >
                  Dismiss all
                </button>
                {skippedCount > 5 && (
                  <Link
                    href="/programs/change"
                    className="px-3 py-1.5 rounded border border-slate-700 text-slate-400 font-mono text-[10px] uppercase tracking-[0.18em] hover:text-amber-400 hover:border-amber-500/40 transition"
                  >
                    Restart program
                  </Link>
                )}
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
}
